<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue';

const props = defineProps({
  isCollapsed: {
    type: Boolean,
    default: false
  },
  
  showToggle: {
    type: Boolean,
    default: true
  },
  
  sections: {
    type: Array,
    default: () => []
  },

  expandedItems: {
    type: Array,
    default: () => []
  },

  allowMultipleExpanded: {
    type: Boolean,
    default: true
  },

  showDefaultContent: {
    type: Boolean,
    default: false
  },

  selectedItem: {
    type: String,
    default: null
  },

  selectedSubItem: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['toggle-collapse', 'item-toggle', 'subitem-click', 'item-click']);

const internalExpandedItems = ref(props.expandedItems || []);

const findParentItemId = (subItemId) => {
  for (const section of props.sections) {
    for (const item of section.items) {
      if (item.subItems && item.subItems.some(sub => sub.id === subItemId)) {
        return item.id;
      }
    }
  }
  return null;
};

watch(() => props.selectedSubItem, (newSubItemId) => {
  if (newSubItemId) {
    const parentItemId = findParentItemId(newSubItemId);
    if (parentItemId && !internalExpandedItems.value.includes(parentItemId)) {
      if (props.allowMultipleExpanded) {
        internalExpandedItems.value.push(parentItemId);
      } else {
        internalExpandedItems.value = [parentItemId];
      }
      emit('item-toggle', {
        itemId: parentItemId,
        isExpanded: true,
        expandedItems: [...internalExpandedItems.value]
      });
    }
  }
}, { immediate: true });

const toggleCollapse = () => {
  emit('toggle-collapse');
};

const emitItemClick = (item, sectionName) => {
  emit('item-click', {
    item: {
      ...item,
      sectionName: sectionName
    },
    redirect: item.redirect
  });
};

const handleExpandedItem = (itemId) => {
  if (props.allowMultipleExpanded) {
    internalExpandedItems.value.push(itemId);
  } else {
    internalExpandedItems.value = [itemId];
  }
  
  emit('item-toggle', {
    itemId: itemId,
    isExpanded: true,
    expandedItems: [...internalExpandedItems.value]
  });
};

const handleItemClick = (item, sectionName) => {
  if (item.redirect && !hasSubItems(item)) {
    emitItemClick(item, sectionName);
    return;
  }
  
  if (props.isCollapsed) {
    if (hasSubItems(item) && !internalExpandedItems.value.includes(item.id)) {
      handleExpandedItem(item.id);
    }
    toggleCollapse();
    return;
  }
  
  if (hasSubItems(item)) {
    toggleItem(item.id);
  } else if (item.redirect) {
    emitItemClick(item, sectionName);
  }
};

const toggleItem = (itemId) => {
  if (props.allowMultipleExpanded) {
    const index = internalExpandedItems.value.indexOf(itemId);
    if (index > -1) {
      internalExpandedItems.value.splice(index, 1);
    } else {
      internalExpandedItems.value.push(itemId);
    }
  } else {
    internalExpandedItems.value = internalExpandedItems.value.includes(itemId) 
      ? [] 
      : [itemId];
  }
  
  emit('item-toggle', {
    itemId,
    isExpanded: internalExpandedItems.value.includes(itemId),
    expandedItems: [...internalExpandedItems.value]
  });
};

const handleSubItemClick = (sectionName, item, subItem) => {
  const eventData = {
    section: sectionName,
    item: {
      id: item.id,
      title: item.title
    },
    subItem: {
      ...subItem,
      itemId: item.id,
      itemTitle: item.title,
      sectionName: sectionName
    }
  };

  if (subItem.redirect) {
    eventData.redirect = subItem.redirect;
  }

  emit('subitem-click', eventData);
};

const isItemExpanded = (itemId) => {
  return internalExpandedItems.value.includes(itemId);
};

const hasSubItems = (item) => {
  return item.subItems && item.subItems.length > 0;
};

</script>

<template>
  <div class="relative h-full">
    <div class="h-full flex flex-col px-2 py-3 bg-gradient-to-r from-echoes-background-black to-echoes-background-gray/20 border-r border-echoes-border-gray/40 transition-all duration-300 ease-in-out relative overflow-hidden"
         :class="[
           isCollapsed ? 'w-16' : 'w-64',
         ]"
         >

      <!-- Main navigation content -->
      <div class="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
        <nav v-if="sections.length > 0" class="py-4 space-y-6 transition-all duration-300 ease-in-out"
              >
          <!-- Loop through sections -->
          <div v-for="sectionData in sections" :key="sectionData.section" class="space-y-2">
            
            <!-- Section Title -->
            <div class=" px-2 mb-2 transition-all duration-300 ease-in-out"
              >
              <span class="text-xs uppercase text-echoes-primary font-semibold whitespace-nowrap opacity-100">
                {{ sectionData.section }}
              </span>
            </div>
            
            <!-- Items in this section -->
            <div class="space-y-2">
              <div v-for="item in sectionData.items" :key="item.id" class="relative">
                
                <!-- Main item button -->
                <button
                  @click="handleItemClick(item, sectionData.section)"
                  class="w-full p-2 transition-all hover:bg-white/5 duration-200 group relative flex items-center rounded-md"
                  :class="{
                    'text-white bg-white/10': props.selectedItem === item.id || (hasSubItems(item) && item.subItems.some(sub => sub.id === props.selectedSubItem)),
                    'text-white hover:text-white hover:bg-white/5': props.selectedItem !== item.id && !isCollapsed,
                    'text-white hover:text-white hover:bg-white/5': props.selectedItem !== item.id && isCollapsed,
                    'text-white': !isCollapsed && isItemExpanded(item.id) && props.selectedItem !== item.id
                  }"
                  :data-testid="'menu-item-' + item.id"
                >
                  <!-- Item icon -->
                  <div class="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <!-- SVG Icon -->
                    <div v-if="item.svg" 
                         class=" transition-colors duration-200 flex items-center justify-center"
                         :class="{
                           'text-white': props.selectedItem === item.id,
                           'text-white group-hover:text-white': props.selectedItem !== item.id
                         }"
                         v-html="item.svg">
                    </div>
                    
                    <!-- CSS Icon (fallback) -->
                    <i v-else-if="item.icon" :class="[
                         item.icon,
                         'transition-colors duration-200',
                         {
                           'text-white': props.selectedItem === item.id,
                           'text-white group-hover:text-white': props.selectedItem !== item.id
                         }
                       ]"></i>
                  </div>
                  
                  <!-- Item title and expand arrow -->
                  <div class="ml-2 flex items-center justify-between flex-1 overflow-hidden transition-all duration-300 ease-in-out"
                       :class="isCollapsed ? 'opacity-0 pointer-events-none w-0' : 'opacity-100'">
                    <span class="font-medium text-sm whitespace-nowrap truncate">
                      {{ item.title }}
                    </span>
                    
                    <!-- Only show chevron if item has sub-items -->
                    <i v-if="hasSubItems(item)" 
                       class="mdi mdi-chevron-down text-white/80 transition-transform duration-200 flex-shrink-0 ml-2"
                       :class="{ 'rotate-180': isItemExpanded(item.id) }"></i>
                  </div>
                </button>

                <!-- Tooltip for collapsed state -->
                <div v-if="isCollapsed" 
                     class="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-white/80 hover:text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {{ item.title }}
                  <span v-if="hasSubItems(item)" class="ml-1 text-xs">(expand)</span>
                </div>

                <!-- Sub-items (expanded state) -->
                <div v-if="hasSubItems(item) && isItemExpanded(item.id) && !isCollapsed" 
                     class="mt-2 overflow-hidden transition-all duration-300 ease-in-out">
                  <div class="space-y-1 relative">
                    <div class="absolute left-5 top-0 bottom-0 w-px bg-echoes-border-gray"></div>
                    
                    <template v-for="(subItem) in item.subItems" :key="subItem.id">
                      <!-- Divider for subsection -->
                      <div
                        v-if="subItem.isDivider"
                        class="w-full pl-10 pr-3 py-2"
                      >
                        <div class="text-xs uppercase text-echoes-primary font-semibold tracking-wider opacity-100">
                          {{ subItem.label }}
                        </div>
                      </div>
                      
                      <!-- Regular sub-item button -->
                      <button
                        v-else
                        @click="handleSubItemClick(sectionData.section, item, subItem)"
                        class="ml-10 mr-2 px-3 py-1 rounded-md transition-all duration-200 group flex items-center w-[calc(100%-2rem-0.5rem)]"
                        :class="{
                          'text-white bg-white/10': props.selectedSubItem === subItem.id,
                          'text-white hover:text-white hover:bg-white/5': props.selectedSubItem !== subItem.id
                        }"
                        :data-testid="'menu-item-' + subItem.id"
                      >
                        <!-- Sub-item icon -->
                        <div class="w-6 h-6 flex items-center justify-center">
                          <div v-if="subItem.svg" 
                               style="color: #FFFFFF99;"
                               v-html="subItem.svg">
                          </div>
                          
                          <i v-else-if="subItem.icon" :class="[
                               subItem.icon,
                               'text-base transition-colors duration-200 bg-transparent'
                             ]"
                             style="color: #FFFFFF99;"></i>
                        </div>
                        
                        <!-- Sub-item label -->
                        <span class="ml-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap truncate"
                              style="color: #FFFFFF99;">
                          {{ subItem.label }}
                        </span>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <!-- Custom content slot -->
        <div class="px-4 transition-all duration-300 ease-in-out"
             :class="isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'">
          <slot name="content">
            <div v-if="showDefaultContent && !isCollapsed">
              <div class="py-4 mt-4">
                <p class="text-sm text-gray-600">Custom sidebar content</p>
              </div>
            </div>
          </slot>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex-shrink-0 transition-all duration-300 ease-in-out"
           :class="[
             isCollapsed ? 'p-2' : 'p-4'
           ]">
        <div class="transition-all duration-300 ease-in-out"
             :class="isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'">
          <slot name="footer"></slot>
        </div>
        
        <div v-if="isCollapsed" class="flex justify-center">
          <slot name="footer-collapsed"></slot>
        </div>
      </div>

      <!-- Toggle button at bottom -->
      <div v-if="showToggle" class="absolute left-3 right-0 bottom-3 hidden">
        <div class="flex">
          <button
            @click="toggleCollapse"
            class="w-10 h-10 text-white/80 hover:text-white flex items-center justify-center rounded-md bg-echoes-border-gray hover:bg-white/10 transition-colors duration-200 flex-shrink-0"
            :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          >
            <i class="uil uil-web-section "></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(svg:not(.eval-icon-no-fill)) {
  fill: currentColor;
  stroke: currentColor;
}

:deep(svg:not(.eval-icon-no-fill) *) {
  fill: inherit;
  stroke: inherit;
}

:deep(svg.eval-icon-no-fill) {
  fill: none !important;
}

:deep(svg.eval-icon-no-fill *) {
  fill: none !important;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>